<template>
  <v-container>
    <v-col class="text-right">
      <v-btn fab small color="primary" @click="showUploadImagesDialog = true">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-col>
    <v-container fluid>
      <div
        v-if="currentArtwork.photos === null || currentArtwork.photos.length == 0"
        class="text-caption">
        {{ $t('gallery.no_photos') }}
      </div>
      <v-row dense>
        <v-col
          v-for="(photo, index) in currentArtwork.photos"
          :key="index"
          cols="6"
          md="3"
          sm="4">
          <v-card
            tile
            flat
            align="center"
            justify="center"
            class="grey lighten-4">
            <Thumbnail
              :fileId="photo.thumbnailFileId"
              :contain="false"
              :width="null"
              :height="150">
            </Thumbnail>
            <v-card-text class="text-caption">
              {{ photo.fileName }}
            </v-card-text>
            <v-card-actions>
              <v-tooltip v-if="photo.isCoverPhoto" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on">mdi-camera-outline</v-icon>
                </template>
                {{ $t('gallery.is_cover_photo_hint') }}
              </v-tooltip>
              <v-tooltip v-if="photo.isExportable" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on">mdi-file-export-outline</v-icon>
                </template>
                {{ $t('gallery.is_exportable_hint') }}
              </v-tooltip>
              <v-spacer></v-spacer>
              <v-menu>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    v-bind="attrs"
                    v-on="on">
                    <v-icon>
                      mdi-pencil-outline
                    </v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item v-if="photo.isExportable" link @click="unsetExportable(photo)">
                    <v-list-item-icon>
                      <v-icon>mdi-file-export-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      {{ $t('gallery.command_unset_exportable') }}
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-else link @click="setExportable(photo)">
                    <v-list-item-icon>
                      <v-icon>mdi-file-export-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      {{ $t('gallery.command_set_exportable') }}
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item link @click="setCover(photo)">
                    <v-list-item-icon>
                      <v-icon>mdi-camera-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      {{ $t('gallery.command_set_cover') }}
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item link @click="downloadPhoto(photo)">
                    <v-list-item-icon>
                      <v-icon>mdi-download-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      {{ $t('gallery.command_download') }}
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item link @click="deletePhoto(photo)">
                    <v-list-item-icon >
                      <v-icon color="error">mdi-delete-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content class="error--text">
                      {{ $t('gallery.command_delete') }}
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <UploadFileDialog
      :dialog.sync="showUploadImagesDialog"
      :description="$t('gallery.drag_and_drop_images')"
      :multiple="true"
      @filesUploaded="processImagesUpload($event)">
    </UploadFileDialog>
    <confirm-dialog :show-dialog="this.showConfirmDeleteDialog"
      confirm-color="error"
      :question-text="$t('common_actions.confirm_delete')"
      :positive-text="$t('common_actions.delete')"
      :negative-text="$t('common_actions.cancel')"
      @onCancel="cancelDeleteItem"
      @onConfirm="confirmDeleteItem">
    </confirm-dialog>
  </v-container>
</template>

<script>
import UploadFileDialog from '@/components/common/UploadFileDialog.vue'
import Thumbnail from '@/components/common/Thumbnail.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import FileService from '@/services/file/FileService'

import api from '@/services/api/api'

export default {
  components: { UploadFileDialog, Thumbnail, ConfirmDialog },
  data () {
    return {
      showUploadImagesDialog: false,
      showConfirmDeleteDialog: false,
      photoToDelete: null
    }
  },
  computed: {
    currentArtwork () {
      return this.$store.getters['artworks/currentArtwork']
    }
  },
  methods: {
    processImagesUpload (imageList) {
      this.$store.dispatch('artworks/createUploadSession').then(
        _ => {
          var promises = []
          imageList.forEach(i => promises.push(this.$store.dispatch('artworks/uploadImage', i)))

          Promise.all(promises)

          this.$emit('onChange')
        })
    },
    setExportable (photo) {
      photo.isExportable = true
      this.$emit('onChange')
    },
    unsetExportable (photo) {
      photo.isExportable = false
      this.$emit('onChange')
    },
    setCover (photo) {
      this.currentArtwork.photos.forEach(p => { p.isCoverPhoto = false })

      photo.isCoverPhoto = true
      this.currentArtwork.thumbnailFileId = photo.thumbnailFileId

      this.$emit('onChange')
    },
    deletePhoto (photo) {
      this.photoToDelete = photo
      this.showConfirmDeleteDialog = true
    },
    cancelDeleteItem () {
      this.showConfirmDeleteDialog = false
      this.photoToDelete = null
    },
    confirmDeleteItem () {
      this.currentArtwork.photos.splice(this.currentArtwork.photos.indexOf(this.photoToDelete), 1)

      var thumbnailPhoto = this.currentArtwork.photos.find(p => p.isCoverPhoto)

      if (thumbnailPhoto == null) {
        this.currentArtwork.thumbnailFileId = -1
      }

      this.showConfirmDeleteDialog = false
      this.photoToDelete = null

      this.$emit('onChange')
    },
    downloadPhoto (photo) {
      FileService.openDownload(photo.fileId).then(tempId => {
        window.open(api.defaults.baseURL + '/download/' + tempId)
      })
    }
  }
}

</script>



// WEBPACK FOOTER //
// src/components/gallery/PhotoGallery.vue